//src/superAdmin/ThemeProvider.tsx
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/store";
import { API_V1_BASE_URL } from "../../config/api";

interface ThemeContextValue {
  primaryColor: string;
  secondaryColor: string;
  loading: boolean;
}

const ThemeContext = createContext<ThemeContextValue>({
  primaryColor: "#2563EB",
  secondaryColor: "#F97316",
  loading: true,
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const tenantId = useSelector((state: RootState) => state.auth.user?.tenantId || "");
  const [theme, setTheme] = useState<ThemeContextValue>({
    primaryColor: "#2563EB",
    secondaryColor: "#F97316",
    loading: true,
  });

  useEffect(() => {
    if (!tenantId) return;

    const applyTheme = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const res = await fetch(`${API_V1_BASE_URL}/theme`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const json = await res.json();
        const { primaryColor, secondaryColor } = json.data;
        document.documentElement.style.setProperty("--color-primary", primaryColor);
        document.documentElement.style.setProperty("--color-secondary", secondaryColor);
        setTheme({ primaryColor, secondaryColor, loading: false });
      } catch (err) {
        console.error("Failed to load theme, using defaults", err);
        setTheme((prev) => ({ ...prev, loading: false }));
      }
    };
    applyTheme();
  }, [tenantId]);

  return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
};